import { useEffect, useState } from "react";
import { adminApi } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { LogOut, Download, RefreshCw, Plus } from "lucide-react";

export default function Admin() {
  const { t } = useLang();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [environment, setEnvironment] = useState("production");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [devLog, setDevLog] = useState([]);
  const [newEntry, setNewEntry] = useState({ title: "", version: "", notes_en: "", occurred_at: "" });
  const [loading, setLoading] = useState(false);

  // Bootstrap: verify existing cookie
  useEffect(() => {
    (async () => {
      try { await adminApi.get("/admin/me"); setAuthed(true); }
      catch { setAuthed(false); }
      finally { setChecking(false); }
    })();
  }, []);

  const login = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post("/admin/login", { email, password });
      setAuthed(true); setPassword("");
    } catch (err) {
      const code = err?.response?.status;
      if (code === 429) toast.error("Too many attempts. Please try again later.");
      else toast.error(t.admin.unauthorized);
    }
  };

  const logout = async () => {
    try { await adminApi.post("/admin/logout"); } catch {}
    setAuthed(false); setData(null); setDevLog([]);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("environment", environment);
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      const [a, d] = await Promise.all([
        adminApi.get(`/admin/analytics?${params.toString()}`),
        adminApi.get(`/admin/dev-log`),
      ]);
      setData(a.data); setDevLog(d.data.entries || []);
    } catch (e) {
      if (e?.response?.status === 401) { setAuthed(false); }
      else toast.error("Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (authed) loadAll(); /* eslint-disable-next-line */ }, [authed]);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      params.set("environment", environment);
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      const r = await adminApi.get(`/admin/events/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `mift_events_${environment}.csv`; a.click();
    } catch { toast.error("Export failed"); }
  };

  const addDevLog = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post(`/admin/dev-log`, newEntry);
      toast.success("Milestone recorded");
      setNewEntry({ title: "", version: "", notes_en: "", occurred_at: "" });
      const d = await adminApi.get(`/admin/dev-log`);
      setDevLog(d.data.entries || []);
    } catch { toast.error("Save failed"); }
  };

  if (checking) return <div className="max-w-md mx-auto px-5 py-24 text-slate-400">…</div>;

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-5 lg:px-8 py-24" data-testid="admin-login-page">
        <h1 className="text-3xl font-bold">{t.admin.login_title}</h1>
        <form onSubmit={login} className="mt-8 card-mift p-6 grid gap-4" data-testid="admin-login-form">
          <div><Label className="text-slate-300">{t.admin.email}</Label>
            <Input data-testid="admin-email" type="email" required autoComplete="email"
                   className="mt-1 bg-[#0A1224] border-slate-700 text-slate-100"
                   value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          <div><Label className="text-slate-300">{t.admin.password}</Label>
            <Input data-testid="admin-password" type="password" required autoComplete="current-password"
                   className="mt-1 bg-[#0A1224] border-slate-700 text-slate-100"
                   value={password} onChange={(e) => setPassword(e.target.value)} /></div>
          <button className="btn-primary w-max" data-testid="admin-sign-in">{t.admin.sign_in}</button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14" data-testid="admin-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold">{t.admin.title}</h1>
        <button className="btn-ghost inline-flex items-center gap-2" onClick={logout} data-testid="admin-sign-out">
          <LogOut className="w-4 h-4" /> {t.admin.sign_out}
        </button>
      </div>

      <div className="mt-8 card-mift p-5 flex flex-wrap items-end gap-3" data-testid="admin-filters">
        <div>
          <Label className="text-slate-300 text-xs">{t.admin.env_label}</Label>
          <Select value={environment} onValueChange={setEnvironment}>
            <SelectTrigger data-testid="filter-env" className="mt-1 w-40 bg-[#0A1224] border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0A1224] border-slate-700 text-slate-100">
              <SelectItem value="production">{t.admin.env_production}</SelectItem>
              <SelectItem value="test">{t.admin.env_test}</SelectItem>
              <SelectItem value="all">{t.admin.env_all}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-slate-300 text-xs">{t.admin.start_date}</Label>
          <Input type="date" data-testid="filter-start" className="mt-1 w-44 bg-[#0A1224] border-slate-700 text-slate-100"
                 value={start} onChange={(e) => setStart(e.target.value)} /></div>
        <div><Label className="text-slate-300 text-xs">{t.admin.end_date}</Label>
          <Input type="date" data-testid="filter-end" className="mt-1 w-44 bg-[#0A1224] border-slate-700 text-slate-100"
                 value={end} onChange={(e) => setEnd(e.target.value)} /></div>
        <button className="btn-primary inline-flex items-center gap-2" onClick={loadAll} data-testid="filter-apply" disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> {t.admin.apply}
        </button>
        <button className="btn-ghost inline-flex items-center gap-2" onClick={exportCsv} data-testid="admin-export-csv">
          <Download className="w-4 h-4" /> {t.admin.export_csv}
        </button>
      </div>

      {data && (
        <>
          <div className="mt-8 flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-blue-300">{t.admin.totals}</span>
            <span className="text-xs px-2 py-0.5 rounded-full border border-slate-700 text-slate-400">{data.environment}</span>
          </div>
          {Object.values(data.totals).every((v) => !v) && (
            <p className="mt-3 text-sm text-slate-500 italic">{t.admin.no_data}</p>
          )}
          <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {Object.entries(data.totals).map(([k, v]) => (
              <div key={k} className="card-mift p-5" data-testid={`admin-total-${k}`}>
                <div className="text-xs uppercase tracking-widest text-slate-500">{k.replace(/_/g, " ")}</div>
                <div className="mt-2 text-3xl font-semibold text-blue-300">{v}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {[["by_country", t.admin.by_country], ["by_field", t.admin.by_field], ["by_language", t.admin.by_language]].map(([k, l]) => (
              <div key={k} className="card-mift p-5">
                <div className="text-xs uppercase tracking-widest text-blue-300 mb-4">{l}</div>
                <div className="space-y-2 text-sm text-slate-300 max-h-80 overflow-auto">
                  {Object.entries(data[k] || {}).map(([kk, vv]) => (
                    <div key={kk} className="flex justify-between border-b border-slate-800 pb-1"><span>{kk}</span><span className="text-blue-300">{vv}</span></div>
                  ))}
                  {!Object.keys(data[k] || {}).length && <div className="text-slate-500 italic text-xs">—</div>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="mt-14">
        <h2 className="text-xl font-semibold text-blue-300">{t.admin.dev_log_title}</h2>
        <div className="mt-4 grid lg:grid-cols-2 gap-6">
          <div className="card-mift p-5" data-testid="dev-log-list">
            {devLog.length === 0 && <p className="text-slate-500 italic text-sm">—</p>}
            <ul className="space-y-3">
              {devLog.map((e) => (
                <li key={e.id} className="border border-slate-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-slate-100">{e.title}</div>
                    {e.version && <span className="text-xs px-2 py-0.5 rounded-full border border-blue-500/30 text-blue-300">{e.version}</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{(e.occurred_at || "").slice(0, 10)}</div>
                  {e.notes_en && <p className="mt-2 text-sm text-slate-300">{e.notes_en}</p>}
                </li>
              ))}
            </ul>
          </div>
          <form onSubmit={addDevLog} className="card-mift p-5 grid gap-3" data-testid="dev-log-form">
            <div className="flex items-center gap-2 text-blue-300 text-sm"><Plus className="w-4 h-4" /> {t.admin.dev_log_add}</div>
            <div><Label className="text-slate-300 text-xs">{t.admin.dev_log_title_field} *</Label>
              <Input required data-testid="dev-log-title" className="mt-1 bg-[#0A1224] border-slate-700 text-slate-100"
                     value={newEntry.title} onChange={(e) => setNewEntry({ ...newEntry, title: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300 text-xs">{t.admin.dev_log_version}</Label>
                <Input data-testid="dev-log-version" className="mt-1 bg-[#0A1224] border-slate-700 text-slate-100"
                       value={newEntry.version} onChange={(e) => setNewEntry({ ...newEntry, version: e.target.value })} /></div>
              <div><Label className="text-slate-300 text-xs">{t.admin.dev_log_occurred}</Label>
                <Input type="date" data-testid="dev-log-occurred" className="mt-1 bg-[#0A1224] border-slate-700 text-slate-100"
                       value={newEntry.occurred_at} onChange={(e) => setNewEntry({ ...newEntry, occurred_at: e.target.value })} /></div>
            </div>
            <div><Label className="text-slate-300 text-xs">{t.admin.dev_log_notes}</Label>
              <Textarea rows={3} data-testid="dev-log-notes" className="mt-1 bg-[#0A1224] border-slate-700 text-slate-100"
                        value={newEntry.notes_en} onChange={(e) => setNewEntry({ ...newEntry, notes_en: e.target.value })} /></div>
            <button className="btn-primary w-max" data-testid="dev-log-save">{t.admin.dev_log_save}</button>
          </form>
        </div>
      </div>
    </div>
  );
}
